// The startpage itself: a responsive grid of liquid-glass widget cards.
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/core/lib/api-client";
import type { Startpage } from "@/core/types";
import { GlassSurface } from "@/ui/GlassSurface";
import { WidgetView } from "@/widgets/builtins";
import { useAuth } from "@/features/auth/AuthContext";

export function Dashboard() {
  const { user } = useAuth();
  const [page, setPage] = useState<Startpage | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) return;
    api
      .listMine()
      .then(async (pages) => {
        if (pages.length > 0) return pages[0];
        // First run: create a starter page with a couple of widgets.
        return api.create({
          title: "My Startpage",
          is_public: false,
          tags: ["general"],
          widgets: [
            { id: "w1", kind: "clock", config: {}, x: 0, y: 0, w: 2, h: 1, source: null },
            { id: "w2", kind: "weather", config: { label: "New York" }, x: 2, y: 0, w: 2, h: 2, source: null },
            { id: "w3", kind: "news", config: { topic: "world" }, x: 0, y: 1, w: 3, h: 3, source: null },
          ],
        });
      })
      .then(setPage)
      .finally(() => setLoading(false));
  }, [user]);

  if (loading) return <p className="p-8 opacity-70">Loading your startpage…</p>;
  if (!page) return <p className="p-8 opacity-70">No startpage found.</p>;

  return (
    <div className="mx-auto max-w-6xl p-4">
      <header className="mb-6 flex items-center justify-between">
        <h1 className="text-2xl font-semibold">{page.title}</h1>
        <div className="flex items-center gap-3 text-sm">
          <Link to="/discover" className="opacity-80 hover:opacity-100">
            Discover
          </Link>
          <Link to="/vr" className="opacity-80 hover:opacity-100">
            Enter VR
          </Link>
        </div>
      </header>

      <div className="grid grid-cols-2 gap-4 sm:grid-cols-4 lg:grid-cols-6">
        {page.widgets.map((w) => (
          <GlassSurface
            key={w.id}
            className="min-h-24"
            style={{ gridColumn: `span ${Math.min(w.w, 6)}`, gridRow: `span ${w.h}` }}
            refraction={w.w * w.h >= 4}
          >
            <WidgetView widget={w} />
          </GlassSurface>
        ))}
      </div>
    </div>
  );
}
